<script setup lang="ts">
import { storeToRefs } from 'pinia'
import { useI18n } from 'vue-i18n'
import { VehicleManagementView } from '~/interfaces/vehicles'
import useSimulationStore from '~/store/simulation'
import useVehiclesStore from '~/store/vehicles'
import useUserStore from '~/store/user'
import type { UserVehicle } from '#cmui/types/vehicle'

const simulationStore = useSimulationStore()
const vehiclesStore = useVehiclesStore()
const userStore = useUserStore()

const { t } = useI18n()
const toast = useToast()

const { brandsOptions, modelsOptions, versionsOptions } =
    storeToRefs(vehiclesStore)

const emits = defineEmits(['showSimulatorPanel'])

const handleSetVehicleAsDefault = async (versionId: number) => {
    await simulationStore.setVehicleAsDefault(versionId)

    toast.success(t('user_vehicles.toasts.default'))

    setTimeout(() => {
        emits('showSimulatorPanel')
    }, 300)
}

const handleVehicleSelection = async (versionId: number) => {
    simulationStore.selectVehicle(versionId)

    setTimeout(() => {
        emits('showSimulatorPanel')
    }, 300)
}

const handleVehicleDeletion = (versionId: number) => {
    vehiclesStore.vehicleForDeletion =
        simulationStore.vehicles.find(
            (vehicle) => vehicle.versionId === versionId
        ) || null

    vehiclesStore.setVehicleManagementView(VehicleManagementView.DELETE)
}

const openVehicleForm = (versionId?: number, isUpdate = false) => {
    if (versionId) {
        vehiclesStore.vehicleForModification =
            simulationStore.vehicles.find(
                (vehicle) => vehicle.versionId === versionId
            ) || null
    }

    vehiclesStore.meta.isModificationForm = isUpdate
    vehiclesStore.setVehicleManagementView(VehicleManagementView.FORM)
}

const handleConfirmDeletion = async () => {
    if (vehiclesStore.vehicleForDeletion) {
        try {
            await simulationStore.deleteVehicle(
                vehiclesStore.vehicleForDeletion.versionId,
                true
            )
            toast.success(t('user_vehicles.toasts.delete'))
        } catch {
            toast.error(t('user_vehicles.toasts.delete_failed'))
        } finally {
            vehiclesStore.vehicleForDeletion = null
            vehiclesStore.setVehicleManagementView(VehicleManagementView.LIST)
        }
    }
}

const handleFormSubmission = async (vehicle: UserVehicle) => {
    // we submit the form if the vehicle is not already in the user's vehicles list
    if (
        !simulationStore.simulator.userVehicles.some(
            (v) => v.vehicle.versionId === vehicle.versionId
        )
    ) {
        await simulationStore.submitVehicleForm(vehicle)
        toast.success(
            t(
                `user_vehicles.toasts.${vehiclesStore.vehicleForModification ? 'modify' : 'add'}`
            )
        )
    } else {
        toast.error(t('user_vehicles.toasts.already_exists'))
    }

    vehiclesStore.vehicleForModification = null
    vehiclesStore.setVehicleManagementView(VehicleManagementView.LIST)
}

onMounted(async () => {
    if (vehiclesStore.brands.length === 0) {
        await vehiclesStore.fetchVehicleBrands()
    }

    if (!simulationStore.simulator.currentVehicle?.isIncompatible) {
        await simulationStore.fetchVehiclesCompatibilities()
    }
})
</script>

<template>
    <div
        class="flex flex-col justify-center items-center gap-6 py-8 px-6 bg-white overflow-y-auto"
    >
        <div
            v-if="
                vehiclesStore.isVehicleListView &&
                simulationStore.meta.isLoading
            "
            class="flex flex-col mb-auto items-center w-full gap-2 mt-[100px]"
        >
            <ChargemapSkeletonLoader
                v-for="i in userStore.userVehicles.length"
                :key="i"
                class="w-full h-[80px] rounded-lg 425:rounded-2xl"
            />
        </div>
        <SimulationVehiclesList
            v-if="
                vehiclesStore.isVehicleListView &&
                !simulationStore.meta.isLoading
            "
            class="mb-auto h-full"
            :vehicles="simulationStore.vehicles"
            :has-incompatibility="simulationStore.hasIncompatibleVehicle"
            @selectVehicle="handleVehicleSelection($event)"
            @deleteVehicle="handleVehicleDeletion($event)"
            @modifyVehicle="openVehicleForm($event, true)"
            @setVehicleAsDefault="handleSetVehicleAsDefault($event)"
            @addVehicle="openVehicleForm()"
        />
        <VehiclesDeletionView
            v-if="
                vehiclesStore.isVehicleDeletionView &&
                vehiclesStore.vehicleForDeletion
            "
            class="mb-auto h-full"
            :brand="vehiclesStore.vehicleToDeleteBrand"
            :model="vehiclesStore.vehicleToDeleteModel"
            @cancel="
                vehiclesStore.setVehicleManagementView(
                    VehicleManagementView.LIST
                )
            "
            @confirm="handleConfirmDeletion()"
        />
        <VehiclesFormView
            v-else-if="vehiclesStore.isVehicleFormView"
            class="mb-auto h-full"
            :vehicle="vehiclesStore.vehicleForModification || undefined"
            :brands="brandsOptions"
            :models="modelsOptions"
            :versions="versionsOptions"
            :is-update="vehiclesStore.meta.isModificationForm"
            :is-disabled="simulationStore.meta.isLoading"
            @updateModels="vehiclesStore.selectBrand($event)"
            @updateVersions="vehiclesStore.selectModel($event)"
            @submit="handleFormSubmission($event)"
        />
    </div>
</template>
